import { z } from 'zod';
import { languageCodes } from '../../src/texts/languages';
import { environmentKeySchema, httpsUrlSchema, siteUrlSchema } from './validators';

export { environmentKeySchema };
export type { EnvironmentKey } from './validators';

const hasNoDuplicates = (values: readonly string[]): boolean => new Set(values).size === values.length;

export const environmentConfigSchema = z.object({
  environment: environmentKeySchema,
  sentry: z.object({
    dsn: z.string().url(),
    tracesSampleRate: z.number().finite().min(0).max(1),
    tracePropagationTargets: z.array(httpsUrlSchema).min(1),
  }),
  debug: z.boolean().default(false),
  catalogUrl: z.string().optional(),
  /**
   * Base URL of the Content CDN bucket serving Product Photos and Product Documents (no trailing slash).
   * A site-relative path (`/content-cdn/...`) goes through this server's Content CDN proxy.
   */
  contentCdnUrl: z.union([httpsUrlSchema, z.string().regex(/^\/[^/]/, 'Must be https:// URL or a site-relative path')]),
  /** Origin of the public site for absolute canonical, hreflang and Open Graph URLs (no trailing slash). */
  siteUrl: siteUrlSchema,
  /** Languages that are live: hreflang sets, sitemaps and indexing are limited to these (two-wave cutover). */
  liveLanguages: z
    .array(z.enum(languageCodes))
    .min(1)
    .refine(hasNoDuplicates, { message: 'Each language may be listed only once' }),
  /** Whether live pages may be indexed; false on the beta (X-Robots-Tag: noindex) until cutover. */
  indexable: z.boolean().default(false),
});

export const configMetaSchema = environmentConfigSchema.partial().extend({
  environment: z.union([environmentKeySchema, z.string().min(1)]).optional(),
});

export type EnvironmentConfig = z.infer<typeof environmentConfigSchema>;
export type ConfigMeta = z.infer<typeof configMetaSchema>;

export const parseEnvironmentConfig = (input: unknown): EnvironmentConfig => environmentConfigSchema.parse(input);

export const parseConfigMeta = (input: unknown): ConfigMeta => configMetaSchema.parse(input);
